import type { PlanNode } from '@timelog/pm-shared';

import type { NodePatch } from './treeOps';

/**
 * The end date and the target date are one day, kept in step on every edit
 * (ADR 0051). Until 2026-10-29 `targetDate` was "the deadline signal,
 * independent of `endDate`", and the two drifted apart on every surface: the
 * list's date column moved one, the panel's Start – end row and the timeline
 * bar moved the other, and the pilot read the disagreement as a bug.
 *
 * This is the ONE place the rule lives. Every patch the editor applies to a
 * node — `SET_NODE_FIELDS` from a cell, `BULK_SET_FIELDS`, `SET_NODE_DATES`
 * from a bar drag or a track click — is merged through `applyNodePatch`, so no
 * surface can write one date without the other following (the ADR 0028
 * lesson: one gate, never a copy per caller).
 *
 * The rule, in order:
 *   1. A patch that SETS `endDate` to a new value writes the same value to
 *      `targetDate`. When a patch names both with different values the end
 *      wins — the schedule is what the bar draws, and no surface does this.
 *   2. Otherwise a patch that SETS `targetDate` to a new value writes it to
 *      `endDate` — but only when the node already HAS an end. A task with no
 *      schedule yet keeps its default span, today → target (ADR 0050); giving
 *      it an end with no start would erase that bar and claim a decision
 *      nobody made.
 *   3. Clearing (an explicit `undefined`) never propagates: emptying the end
 *      field hands the task back to its today → target default, and emptying
 *      the target leaves the schedule in place.
 *
 * Nothing here touches stored data. A document whose two dates already
 * disagree keeps them until one is edited — the Project Ship fixture's
 * numbers are the spec's worked examples and stay as they are.
 */
export const applyNodePatch = (node: PlanNode, patch: NodePatch): PlanNode => {
  const next: PlanNode = { ...node, ...patch };

  if (patch.endDate !== undefined && patch.endDate !== node.endDate) {
    next.targetDate = patch.endDate;
    return next;
  }

  if (
    patch.targetDate !== undefined &&
    patch.targetDate !== node.targetDate &&
    node.endDate !== undefined
  ) {
    next.endDate = patch.targetDate;
  }

  return next;
};
